import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { useUserProtocolStatus } from './UserProtocolStatusContext';

const UnloggedCountContext = createContext(null);

/**
 * Single source of truth for the unlogged biohacks count.
 * Uses UserProtocolStatusContext for active protocol IDs (no duplicate protocols_status fetch).
 */
export function UnloggedCountProvider({ children }) {
  const [count, setCount] = useState(0);
  const { getActiveProtocolIds, refresh: refreshProtocolStatuses } = useUserProtocolStatus();
  const inFlightRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data: { session } = {} } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user?.id || typeof user.id !== 'string' || user.id === 'undefined' || user.id === 'null') {
        setCount(0);
        return 0;
      }

      const activeProtocolIds = getActiveProtocolIds();
      if (!activeProtocolIds?.length) {
        setCount(0);
        return 0;
      }

      const activeSet = new Set(activeProtocolIds);
      const cacheKey = `unlogged_${user.id}_${[...activeSet].sort().join(',')}`;
      if (inFlightRef.current?.key === cacheKey) {
        return inFlightRef.current.promise;
      }

      const promise = (async () => {
        const { data: scheduleData, error: scheduleError } = await supabase
          .from('logging_schedules')
          .select('protocol_id, hack_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .not('hack_id', 'is', null)
          .in('protocol_id', Array.from(activeSet));

        if (scheduleError || !scheduleData?.length) {
          setCount(0);
          return 0;
        }

        const today = new Date().toISOString().slice(0, 10);
        const { data: logsToday } = await supabase
          .from('logging_hack_entries')
          .select('protocol_id, hack_id')
          .eq('user_id', user.id)
          .eq('date', today);

        const loggedSet = new Set((logsToday || []).map(l => `${l.protocol_id}_${l.hack_id}`));
        const unlogged = scheduleData.filter(
          ({ protocol_id, hack_id }) => !loggedSet.has(`${protocol_id}_${hack_id}`)
        );
        const result = unlogged.length;
        setCount(result);
        return result;
      })().finally(() => {
        if (inFlightRef.current?.key === cacheKey) {
          inFlightRef.current = null;
        }
      });

      inFlightRef.current = { key: cacheKey, promise };
      return promise;
    } catch (err) {
      setCount(0);
      return 0;
    }
  }, [getActiveProtocolIds]);

  useEffect(() => {
    refresh();
  }, [refresh, getActiveProtocolIds]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshProtocolStatuses();
        refresh();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refresh, refreshProtocolStatuses]);

  const value = { count, refresh };
  return (
    <UnloggedCountContext.Provider value={value}>
      {children}
    </UnloggedCountContext.Provider>
  );
}

export function useUnloggedCount() {
  const ctx = useContext(UnloggedCountContext);
  if (!ctx) {
    return { count: 0, refresh: async () => 0 };
  }
  return ctx;
}
